import { useState } from 'react';
import type { LeaderboardEntryDto } from '@cardhub/protocol';
import { api } from '../api/client';
import { Avatar, Empty, ErrorState, Loading, Segmented, TopBar } from '../components/ui';
import { useFormat, useT } from '../i18n';
import { useAsync } from '../lib/hooks';
import { useSession } from '../store/session';

type Period = 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'ALL_TIME';
type Metric = 'WINS' | 'XP' | 'GAMES' | 'COINS_WON';

export function Leaderboards() {
  const t = useT();
  const f = useFormat();
  const me = useSession((s) => s.user)!;
  const [period, setPeriod] = useState<Period>('WEEKLY');
  const [metric, setMetric] = useState<Metric>('WINS');
  const [game, setGame] = useState('ALL');
  const qs = `period=${period}&metric=${metric}&game=${game}`;
  const board = useAsync(() => api.get<{ items: LeaderboardEntryDto[]; total: number }>(`/api/v1/leaderboards?${qs}&limit=50`), [qs]);
  const mine = useAsync(() => api.get<{ rank: number | null; score: number }>(`/api/v1/leaderboards/me?${qs}`), [qs]);
  const medal = (r: number) => (r === 1 ? '🥇' : r === 2 ? '🥈' : r === 3 ? '🥉' : `#${r}`);

  return (
    <main className="page">
      <TopBar title={t('lb.title')} />
      <div className="col" style={{ gap: 8, marginBottom: 12 }}>
        <Segmented label="Period" value={period} onChange={setPeriod} options={[{ value: 'DAILY', label: t('lb.daily') }, { value: 'WEEKLY', label: t('lb.weekly') }, { value: 'MONTHLY', label: t('lb.monthly') }, { value: 'ALL_TIME', label: t('lb.allTime') }]} />
        <Segmented label="Metric" value={metric} onChange={setMetric} options={[{ value: 'WINS', label: t('lb.wins') }, { value: 'XP', label: t('lb.xp') }, { value: 'GAMES', label: t('lb.games') }, { value: 'COINS_WON', label: t('lb.coins') }]} />
        <Segmented
          label="Game"
          value={game}
          onChange={setGame}
          options={[{ value: 'ALL', label: t('lb.all') }, ...(['CALL_BREAK', 'SPADES', 'TARNEEB', 'HEARTS', 'FIVE_HUNDRED', 'JUDGEMENT', 'RUMMY', 'EUCHRE', 'GIN_RUMMY', 'TWENTY_NINE', 'CRAZY_EIGHTS', 'CRIBBAGE', 'BLACKJACK', 'BHABHI', 'PINOCHLE', 'ANDAR_BAHAR', 'BRIDGE', 'SEVENS', 'SCOPA', 'BACCARAT', 'BLUFF', 'DRAGON_TIGER', 'FLUSH', 'COURT_PIECE', 'DEHLA_PAKAD', 'WHIST', 'SEEP', 'BELOTE', 'DURAK', 'PRESIDENT', 'BRISCOLA', 'CASINO_WAR', 'THREE_CARD_POKER', 'GO_FISH', 'TRESSETTE', 'RED_DOG', 'CANASTA', 'SKAT', 'PAI_GOW', 'BIG_TWO', 'WAR', 'RUMMY_500', 'BRAG', 'PITCH', 'HAZARI', 'TEEN_PATTI', 'POKER', 'MARRIAGE'] as const).map((g) => ({ value: g, label: t(`game.${g}`) }))]}
        />
      </div>
      {mine.data?.rank && <div className="card spread" style={{ marginBottom: 12 }}><strong>{t('lb.you', { rank: mine.data.rank })}</strong><span className="num">{f.num(mine.data.score)}</span></div>}
      {board.loading && <Loading />}
      {board.error && <ErrorState message={board.error} onRetry={board.reload} />}
      {board.data && board.data.items.length === 0 && <Empty emoji="🏆" title={t('lb.empty')} />}
      {board.data && board.data.items.length > 0 && (
        <ol className="list" style={{ padding: 0, margin: 0, listStyle: 'none' }}>
          {board.data.items.map((e) => (
            <li key={e.user.id} className="list-item" style={e.user.id === me.id ? { background: 'rgba(34,211,166,0.12)' } : undefined}>
              <strong className="num" style={{ width: 36, textAlign: 'center' }}>{medal(e.rank)}</strong>
              <Avatar avatar={e.user.avatar} size={38} />
              <span className="grow ellipsis">
                <strong>{e.user.username}</strong> <span className="muted">{t('common.level', { n: e.user.level })}</span>
              </span>
              <strong className="num">{f.num(e.score)}</strong>
            </li>
          ))}
        </ol>
      )}
    </main>
  );
}
